import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export type Tone = "neutral" | "success" | "danger" | "warning" | "amber" | "primary" | "info" | "meta" | "muted";

const soft: Record<Tone, string> = {
  neutral: "bg-elevated text-fg/85",
  muted: "bg-elevated/60 text-muted",
  success: "bg-success-soft text-success",
  danger: "bg-danger-soft text-danger",
  warning: "bg-warning-soft text-warning",
  amber: "bg-amber-soft text-amber",
  primary: "bg-primary-soft text-lavender",
  info: "bg-info-soft text-info",
  meta: "bg-[rgba(24,119,242,0.14)] text-[#5b9dff]",
};

const outline: Record<Tone, string> = {
  neutral: "border border-line-strong text-fg/85",
  muted: "border border-line text-muted",
  success: "border border-success/40 text-success",
  danger: "border border-danger/40 text-danger",
  warning: "border border-warning/40 text-warning",
  amber: "border border-amber/40 text-amber",
  primary: "border border-primary/60 text-lavender",
  info: "border border-info/40 text-info",
  meta: "border border-meta/50 text-[#5b9dff]",
};

const dotColor: Record<Tone, string> = {
  neutral: "bg-fg/60",
  muted: "bg-subtle",
  success: "bg-success",
  danger: "bg-danger",
  warning: "bg-warning",
  amber: "bg-amber",
  primary: "bg-lavender",
  info: "bg-info",
  meta: "bg-meta",
};

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  variant?: "soft" | "outline" | "plain";
  dot?: boolean;
  icon?: ReactNode;
  size?: "xs" | "sm";
}

/** Rounded status/category pill. `dot` shows the PlayAd "● Active" dot. */
export function Pill({ tone = "neutral", variant = "soft", dot, icon, size = "sm", className, children, ...rest }: PillProps) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full font-medium leading-none",
        size === "xs" ? "h-5 px-1.5 text-[10.5px]" : "h-6 px-2 text-[11.5px]",
        variant === "soft" && soft[tone],
        variant === "outline" && outline[tone],
        variant === "plain" && cn("px-0", soft[tone].split(" ").find((c) => c.startsWith("text-"))),
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn("size-1.5 shrink-0 rounded-full", dotColor[tone])} />}
      {icon}
      {children}
    </span>
  );
}

/** Small uppercase cadence/kind tag (DAILY / WEEKLY / FLEXIBLE). */
export function Tag({ children, tone = "primary", className }: { children: ReactNode; tone?: Tone; className?: string }) {
  return (
    <span className={cn("inline-flex h-5 items-center rounded-md px-1.5 text-[10px] font-semibold uppercase tracking-[0.06em]", soft[tone], className)}>{children}</span>
  );
}

/** Red notification count badge (rail). */
export function CountBadge({ count, className }: { count: number; className?: string }) {
  if (!count) return null;
  return (
    <span className={cn("grid h-4 min-w-4 place-items-center rounded-full bg-[#e42840] px-1 text-[10px] font-bold leading-none text-white ring-2 ring-rail", className)}>
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={cn("inline-flex h-5 min-w-5 items-center justify-center rounded border border-line-strong bg-surface-2 px-1 font-mono text-[10.5px] text-muted", className)}>{children}</kbd>;
}
